import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfYear, endOfYear, addYears, subYears } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import { TrendingUp, Users, Clock, Calendar, ChevronLeft, ChevronRight, Thermometer, Briefcase, CalendarDays, Download, FileText } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stats() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [teamStats, setTeamStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState([]);
  const [overtimeData, setOvertimeData] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // 'week' or 'month' or 'year'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState('all'); // Filter by user
  const [showWorked, setShowWorked] = useState(true);
  const [showLeave, setShowLeave] = useState(true);
  const [showSick, setShowSick] = useState(true);
  const [showOvertime, setShowOvertime] = useState(true);
  const [exporting, setExporting] = useState(false);
  const statsRef = useRef(null);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const range = getDateRange();
      const response = await axios.get(`${API}/stats/export-pdf`, {
        params: {
          start_date: range.start,
          end_date: range.end,
          period_label: getPeriodLabel()
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistiques-${range.start}-${range.end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      return {
        start: format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    } else if (viewMode === 'year') {
      return {
        start: format(startOfYear(currentDate), 'yyyy-MM-dd'),
        end: format(endOfYear(currentDate), 'yyyy-MM-dd')
      };
    } else {
      return {
        start: format(startOfMonth(currentDate), 'yyyy-MM-dd'),
        end: format(endOfMonth(currentDate), 'yyyy-MM-dd')
      };
    }
  }, [currentDate, viewMode]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setTeam(res.data);
    } catch (err) {
      // Ignore
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const [teamRes, advancedRes, overtimeRes] = await Promise.all([
        axios.get(`${API}/stats/team`),
        axios.get(`${API}/stats/advanced`, {
          params: { start_date: range.start, end_date: range.end }
        }),
        axios.get(`${API}/overtime`, {
          params: { start_date: range.start, end_date: range.end }
        })
      ]);
      setTeamStats(teamRes.data);
      setAdvancedStats(advancedRes.data);
      setOvertimeData(overtimeRes.data || []);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [getDateRange, t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigatePeriod = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else if (viewMode === 'year') {
      setCurrentDate(direction === 'prev' ? subYears(currentDate, 1) : addYears(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : subMonths(currentDate, -1));
    }
  };

  const getPeriodLabel = () => {
    const range = getDateRange();
    if (viewMode === 'week') {
      return `${format(new Date(range.start), 'dd MMM', { locale })} - ${format(new Date(range.end), 'dd MMM yyyy', { locale })}`;
    } else if (viewMode === 'year') {
      return format(currentDate, 'yyyy', { locale });
    }
    return format(currentDate, 'MMMM yyyy', { locale });
  };

  // Filter stats by selected user
  const filteredStats = selectedUser === 'all' 
    ? advancedStats 
    : advancedStats.filter(s => s.user_id === selectedUser);

  // Group overtime by user
  const overtimeByUser = overtimeData.reduce((acc, entry) => {
    if (!acc[entry.user_id]) acc[entry.user_id] = 0;
    acc[entry.user_id] += entry.hours || 0;
    return acc;
  }, {});

  // Prepare chart data with stacked values
  const chartData = filteredStats.map(stat => {
    // Use the daily_hours from the API (user's contractual hours / 5)
    const dailyHours = stat.daily_hours || 8.5;
    const leaveHours = stat.leave_days * dailyHours;
    const sickHours = stat.sick_days * dailyHours;
    const overtimeHours = overtimeByUser[stat.user_id] || 0;
    
    return {
      name: stat.user_name.split(' ')[0],
      fullName: stat.user_name,
      worked: showWorked ? stat.worked_hours : 0,
      leave: showLeave ? leaveHours : 0,
      sick: showSick ? sickHours : 0,
      overtime: showOvertime ? overtimeHours : 0,
      total: (showWorked ? stat.worked_hours : 0) + (showLeave ? leaveHours : 0) + (showSick ? sickHours : 0) + (showOvertime ? overtimeHours : 0),
      contractual: stat.contractual_hours,
      color: stat.color
    };
  });

  // Calculate totals
  const totalOvertime = Object.values(overtimeByUser).reduce((sum, h) => sum + h, 0);
  
  const totals = filteredStats.reduce((acc, stat) => {
    const dailyHours = stat.daily_hours || 8.5;
    const leaveHours = stat.leave_days * dailyHours;
    const sickHours = stat.sick_days * dailyHours;
    const userOvertime = overtimeByUser[stat.user_id] || 0;
    return {
      worked: acc.worked + stat.worked_hours,
      leave: acc.leave + leaveHours,
      sick: acc.sick + sickHours,
      overtime: acc.overtime + userOvertime,
      contractual: acc.contractual + stat.contractual_hours,
      leaveDays: acc.leaveDays + stat.leave_days,
      sickDays: acc.sickDays + stat.sick_days,
      daysWorked: acc.daysWorked + (stat.days_worked || 0),
      periodWorkingDays: stat.period_working_days || 0  // Same for all users in the period
    };
  }, { worked: 0, leave: 0, sick: 0, overtime: 0, contractual: 0, leaveDays: 0, sickDays: 0, daysWorked: 0, periodWorkingDays: 0 });
  
  // Get period working days from first stat (same for all)
  const periodWorkingDays = filteredStats.length > 0 ? (filteredStats[0].period_working_days || 0) : 0;
  
  const totalActive = (showWorked ? totals.worked : 0) + (showLeave ? totals.leave : 0) + (showSick ? totals.sick : 0) + (showOvertime ? totals.overtime : 0);
  const variance = totalActive - totals.contractual;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stats-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('statistics').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">{t('actualVsContractual')}</p>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          {/* Export Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-stats-btn"
          >
            <FileText className={`h-4 w-4 mr-2 ${exporting ? 'animate-spin' : ''}`} />
            {exporting ? 'Export...' : 'Export PDF'}
          </Button>
          
          {/* User filter */}
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-48" data-testid="user-filter">
              <SelectValue placeholder={t('allCollaborators')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allCollaborators')}</SelectItem>
              {team.map(member => (
                <SelectItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View mode selector */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">{t('week')}</SelectItem>
              <SelectItem value="month">{t('month')}</SelectItem>
              <SelectItem value="year">{t('year')}</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigatePeriod('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[180px] text-center font-medium">{getPeriodLabel()}</span>
            <Button variant="outline" size="icon" onClick={() => navigatePeriod('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap border border-border rounded-lg p-4 bg-card">
        <span className="text-sm font-medium">{t('filters')}:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showWorked}
            onChange={(e) => setShowWorked(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Clock className="h-4 w-4 text-primary" />
            {t('hoursWorked')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showLeave}
            onChange={(e) => setShowLeave(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Calendar className="h-4 w-4 text-amber-500" />
            {t('paidLeave')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showSick}
            onChange={(e) => setShowSick(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Thermometer className="h-4 w-4 text-red-500" />
            {t('sickDays')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showOvertime}
            onChange={(e) => setShowOvertime(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-purple-500" />
            {t('overtime')}
          </span>
        </label>
      </div>

      {/* Exportable content */}
      <div ref={statsRef} className="space-y-6 bg-background p-4">
        {/* Period Info Header (for export) */}
        <div className="text-center pb-2 border-b border-border">
          <p className="text-lg font-semibold">{getPeriodLabel()} - {periodWorkingDays} {t('workingDays')}</p>
        </div>

        {/* Overview Stats - Vertical layout with title on top and value below */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CalendarDays className="h-4 w-4 text-slate-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('workingDays')}</span>
            </div>
            <p className="text-2xl font-bold">{periodWorkingDays}</p>
          </div>

          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-xs text-muted-foreground uppercase">{t('hoursWorked')}</span>
            </div>
            <p className="text-2xl font-bold">{totals.worked.toFixed(1)}{t('h')}</p>
          </div>

          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Briefcase className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('daysWorked')}</span>
            </div>
            <p className="text-2xl font-bold">{totals.daysWorked}</p>
          </div>
          
          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Calendar className="h-4 w-4 text-amber-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('paidLeave')}</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{totals.leaveDays}j</p>
          </div>

          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Thermometer className="h-4 w-4 text-red-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('sickDays')}</span>
            </div>
            <p className="text-2xl font-bold text-red-500">{totals.sickDays}j</p>
          </div>
          
          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('overtime')}</span>
            </div>
            <p className={`text-2xl font-bold ${totals.overtime >= 0 ? 'text-purple-600' : 'text-orange-600'}`}>
              {totals.overtime >= 0 ? '+' : ''}{totals.overtime.toFixed(1)}{t('h')}
            </p>
          </div>
          
          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="h-4 w-4 text-slate-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('contractualHours')}</span>
            </div>
            <p className="text-2xl font-bold">{totals.contractual.toFixed(1)}{t('h')}</p>
          </div>
          
          <div className="border border-border rounded-lg p-3 bg-card text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Clock className="h-4 w-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground uppercase">{t('variance')}</span>
            </div>
            <p className={`text-2xl font-bold ${variance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {variance >= 0 ? '+' : ''}{variance.toFixed(1)}{t('h')}
            </p>
          </div>
        </div>

      {/* Hours Chart - Stacked bar showing worked + leave + sick + overtime */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <h3 className="text-lg mb-4">{t('actualVsContractual').toUpperCase()}</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={80}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)'
                }}
                formatter={(value, name) => {
                  const labels = {
                    worked: t('hoursWorked'),
                    leave: t('paidLeave'),
                    sick: t('sickDays'),
                    contractual: t('contractualHours')
                  };
                  return [`${value.toFixed(1)}${t('h')}`, labels[name] || name];
                }}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
              />
              <Legend 
                formatter={(value) => {
                  const labels = {
                    worked: t('hoursWorked'),
                    leave: t('paidLeave'),
                    sick: t('sickDays'),
                    contractual: t('contractualHours')
                  };
                  return labels[value] || value;
                }}
              />
              {showWorked && <Bar dataKey="worked" stackId="a" fill="hsl(var(--primary))" name="worked" />}
              {showLeave && <Bar dataKey="leave" stackId="a" fill="#F59E0B" name="leave" />}
              {showSick && <Bar dataKey="sick" stackId="a" fill="#EF4444" name="sick" />}
              {showOvertime && <Bar dataKey="overtime" stackId="a" fill="#A855F7" name="overtime" />}
              <Bar dataKey="contractual" fill="hsl(var(--muted-foreground))" name="contractual" opacity={0.3} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Stats Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="text-left px-4 py-3">{t('collaborator')}</th>
              <th className="text-left px-4 py-3">{t('workingDays')}</th>
              <th className="text-left px-4 py-3">{t('daysWorked')}</th>
              <th className="text-left px-4 py-3">{t('hoursWorked')}</th>
              <th className="text-left px-4 py-3">{t('paidLeave')}</th>
              <th className="text-left px-4 py-3">{t('sickDays')}</th>
              <th className="text-left px-4 py-3">{t('overtime')}</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">{t('contractualHours')}</th>
              <th className="text-left px-4 py-3">{t('variance')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredStats.map((stat) => {
              const dailyHours = stat.daily_hours || 8;
              const leaveHours = stat.leave_days * dailyHours;
              const sickHours = stat.sick_days * dailyHours;
              const userOvertime = overtimeByUser[stat.user_id] || 0;
              const total = stat.worked_hours + leaveHours + sickHours + userOvertime;
              const statVariance = total - stat.contractual_hours;
              
              return (
                <tr key={stat.user_id}>
                  <td className="px-4 py-3 flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: stat.color }}
                    />
                    <span className="font-medium">{stat.user_name}</span>
                  </td>
                  <td className="px-4 py-3 mono text-muted-foreground">{stat.period_working_days || 0}</td>
                  <td className="px-4 py-3 mono">{stat.days_worked || 0}</td>
                  <td className="px-4 py-3 mono">{stat.worked_hours.toFixed(1)}{t('h')}</td>
                  <td className="px-4 py-3 mono text-amber-600">{stat.leave_days}j ({leaveHours.toFixed(0)}{t('h')})</td>
                  <td className="px-4 py-3 mono text-red-600">{stat.sick_days}j ({sickHours.toFixed(0)}{t('h')})</td>
                  <td className={`px-4 py-3 mono ${userOvertime > 0 ? 'text-purple-600' : userOvertime < 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                    {userOvertime !== 0 ? (userOvertime > 0 ? '+' : '') + userOvertime.toFixed(1) + t('h') : '-'}
                  </td>
                  <td className="px-4 py-3 mono font-semibold">{total.toFixed(1)}{t('h')}</td>
                  <td className="px-4 py-3 mono text-muted-foreground">{stat.contractual_hours.toFixed(1)}{t('h')}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${statVariance >= 0 ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                      {statVariance >= 0 ? '+' : ''}{statVariance.toFixed(1)}{t('h')}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
